import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { CourseFormData, Tutor } from '@/types';
import { courseApi, tutorApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { ImageUploadField } from '@/components/forms/ImageUploadField';
import { FreeOfferingField } from '@/components/forms/FreeOfferingField';
import { CategorySelectField } from '@/components/forms/CategorySelectField';
import { VideoUploadField } from '@/components/forms/VideoUploadField';
import { TagInput } from '@/components/ui/TagInput';
import { useAuthStore } from '@/store/authStore';

const audienceSuggestions = [
  'Kids (Age 7-12)',
  'Open to All',
  'Teens',
  'College Students',
  'Professionals',
];

export const AdminCourseForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin/courses' : '/tutor/courses';
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [isTutorsLoading, setIsTutorsLoading] = useState(true);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const isEditMode = Boolean(id);
  const hasTutorOptions = tutors.length > 0;
  const isTutorSelectionDisabled = isTutorsLoading || !hasTutorOptions;
  const [pendingSectionScrollIndex, setPendingSectionScrollIndex] = useState<number | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CourseFormData>({
    defaultValues: {
      categoryId: '',
      title: '',
      description: '',
      thumbnailKey: '',
      previewVideoKey: '',
      level: 'Beginner',
      audience: [],
      teachingMode: 'Online',
      prerequisite: '',
      price: 0,
      isFree: false,
      showOnLanding: false,
      tutorId: '',
      sections: [],
    },
  });

  const { fields: sections, append: appendSection, remove: removeSection } = useFieldArray({
    control,
    name: 'sections',
  });
  const thumbnailKey = watch('thumbnailKey');
  const previewVideoKey = watch('previewVideoKey');
  const isFree = watch('isFree');
  const watchedSections = watch('sections');
  const totalDurationHours = useMemo(
    () =>
      (watchedSections || []).reduce(
        (total, section) => total + (Number(section?.durationHours) || 0),
        0
      ),
    [watchedSections]
  );

  useEffect(() => {
    if (pendingSectionScrollIndex == null) {
      return;
    }

    const topicElement = document.getElementById(`topic-panel-${pendingSectionScrollIndex}`);
    if (!topicElement) {
      return;
    }

    topicElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingSectionScrollIndex(null);
  }, [pendingSectionScrollIndex, sections.length]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTutors = async () => {
      try {
        const data = await tutorApi.getAllTutors({ signal: controller.signal });
        setTutors(data);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch tutors:', error);
        }
      } finally {
        setIsTutorsLoading(false);
      }
    };

    void fetchTutors();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!isEditMode || !id) {
      setThumbnailPreviewUrl('');
      setVideoPreviewUrl('');
      return;
    }

    const controller = new AbortController();

    const fetchCourse = async () => {
      try {
        const course = await courseApi.getCourseById(id, controller.signal);
        if (!course) {
          return;
        }

        reset({
          categoryId: course.categoryId || '',
          title: course.title,
          description: course.description,
          thumbnailKey: course.thumbnailKey,
          previewVideoKey: course.previewVideoKey || '',
          level: course.level,
          audience: course.audience || [],
          teachingMode: course.teachingMode || 'Online',
          prerequisite: course.prerequisite || '',
          price: course.price ?? 0,
          isFree: course.isFree ?? false,
          showOnLanding: course.showOnLanding ?? false,
          tutorId: course.tutorId ?? '',
          sections: course.sections.map((section) => ({
            id: section.id,
            title: section.title,
            description: section.description ?? '',
            position: section.position,
            durationHours: section.duration != null ? Math.round((section.duration / 3600) * 10) / 10 : 0,
            lessons: section.lessons.map((lesson) => ({
              id: lesson.id,
              title: lesson.title,
              description: lesson.description || '',
              duration: 0,
              position: lesson.position,
              isPreview: lesson.isPreview,
            })),
          })),
        });
        setThumbnailPreviewUrl(course.thumbnailUrl || '');
        setVideoPreviewUrl(course.previewVideoUrl || '');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch course:', error);
        }
      }
    };

    void fetchCourse();
    return () => controller.abort();
  }, [id, isEditMode, reset]);

  const onSubmit = async (data: CourseFormData) => {
    setSubmitError('');
    setIsLoading(true);

    const sectionsPayload = (data.sections || []).map(({ durationHours, ...section }, sectionIndex) => ({
      ...section,
      id: section.id || `topic-${sectionIndex + 1}`,
      position: section.position || sectionIndex + 1,
      duration: Math.round((durationHours ?? 0) * 3600),
      lessons: (section.lessons || []).map((lesson, lessonIndex) => ({
        ...lesson,
        id: lesson.id || `subtopic-${sectionIndex + 1}-${lessonIndex + 1}`,
        position: lesson.position || lessonIndex + 1,
        duration: 0,
        isPreview: Boolean(lesson.isPreview),
      })),
    }));

    const payload = {
      categoryId: data.categoryId,
      title: data.title,
      description: data.description,
      thumbnailKey: data.thumbnailKey,
      previewVideoKey: data.previewVideoKey,
      level: data.level,
      audience: data.audience,
      teachingMode: data.teachingMode,
      prerequisite: data.prerequisite,
      price: data.price ?? 0,
      isFree: Boolean(data.isFree),
      tutorId: data.tutorId || null,
      totalDuration: sectionsPayload.reduce((total, section) => total + (section.duration || 0), 0),
      sections: sectionsPayload,
    };

    try {
      if (isEditMode && id) {
        await courseApi.updateCourse(id, payload);
      } else {
        await courseApi.createCourse(payload);
      }
      navigate(basePath);
    } catch (error) {
      setSubmitError((error as Error).message || 'Failed to save course.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTopic = () => {
    const nextSectionIndex = sections.length;

    appendSection({
      title: '',
      description: '',
      position: nextSectionIndex + 1,
      durationHours: 0,
      lessons: [],
    });

    setPendingSectionScrollIndex(nextSectionIndex);
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div>
        <h1 className="page-heading">{isEditMode ? 'Edit Course' : 'Create Course'}</h1>
        <p className="page-subtitle">
          Build premium course pages with stronger overview details, cleaner audience tagging, and a topic/subtopic curriculum structure.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <h2 className="text-xl font-semibold text-slate-950">Course Information</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <Input
              label="Course Title"
              placeholder="e.g., Complete React Developer Course"
              {...register('title', { required: 'Title is required' })}
              error={errors.title?.message}
            />

            <div>
              <label className="form-label">Level</label>
              <select className="form-control min-h-11" {...register('level', { required: 'Level is required' })}>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <Controller
              control={control}
              name="categoryId"
              rules={{ required: 'Please select a category' }}
              render={({ field }) => (
                <CategorySelectField
                  type="course"
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.categoryId?.message}
                  canManageCategories={isAdmin}
                />
              )}
            />

            <div className="md:col-span-2">
              <label className="form-label">Description</label>
              <textarea
                className="form-control min-h-[140px]"
                placeholder="Describe the learning outcome, the teaching style, and what makes this course worth exploring."
                {...register('description', { required: 'Description is required' })}
              />
              {errors.description ? <p className="mt-1.5 text-sm text-red-600">{errors.description.message}</p> : null}
            </div>

            <div>
              <label className="form-label">Teaching Mode</label>
              <select className="form-control min-h-11" {...register('teachingMode', { required: true })}>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            <Input label="Price (INR)" type="number" placeholder="0" min={0} {...register('price', { valueAsNumber: true })} />

            <div className="md:col-span-2">
              <FreeOfferingField
                checked={Boolean(isFree)}
                onChange={(checked) => setValue('isFree', checked, { shouldDirty: true })}
                itemLabel="course"
              />
            </div>

            <div className="md:col-span-2">
              <Controller
                control={control}
                name="audience"
                rules={{ validate: (value) => value.length > 0 || 'Add at least one audience segment' }}
                render={({ field }) => (
                  <TagInput
                    label="Audience"
                    values={field.value || []}
                    onChange={field.onChange}
                    suggestions={audienceSuggestions}
                    placeholder="Add audience groups"
                    error={errors.audience?.message}
                    helperText="Audience appears on public cards and in the course overview section."
                  />
                )}
              />
            </div>

            <div className="md:col-span-2">
              <label className="form-label">Pre-requisite</label>
              <textarea
                className="form-control min-h-[110px]"
                placeholder="Mention any required knowledge, skills, or eligibility."
                {...register('prerequisite')}
              />
            </div>

            <input type="hidden" {...register('thumbnailKey', { required: 'Course thumbnail is required' })} />
            <div className="md:col-span-2">
              <ImageUploadField
                label="Course Thumbnail"
                folder="course-images"
                imageKey={thumbnailKey || ''}
                previewUrl={thumbnailPreviewUrl}
                error={errors.thumbnailKey?.message}
                helperText="Use a refined landscape cover image to strengthen the course hero and listing card."
                onChange={({ key, url }) => {
                  setValue('thumbnailKey', key, { shouldValidate: true, shouldDirty: true });
                  setThumbnailPreviewUrl(url);
                }}
              />
            </div>

            <input type="hidden" {...register('previewVideoKey')} />
            <div className="md:col-span-2">
              <VideoUploadField
                folder="course-videos"
                videoKey={previewVideoKey || ''}
                previewUrl={videoPreviewUrl}
                onChange={({ key, url }) => {
                  setValue('previewVideoKey', key, { shouldDirty: true });
                  setVideoPreviewUrl(url);
                }}
              />
            </div>

            <div className="md:col-span-2">
              <Controller
                control={control}
                name="tutorId"
                rules={{ required: 'Please select a tutor' }}
                render={({ field }) => (
                  <SearchableSelect
                    label="Tutor"
                    value={field.value || ''}
                    onChange={field.onChange}
                    options={tutors.map((tutor) => ({
                      value: tutor.id,
                      label: tutor.name,
                      description: tutor.title || tutor.expertise?.slice(0, 2).join(', ') || undefined,
                    }))}
                    placeholder={isTutorsLoading ? 'Loading approved tutors...' : 'Select a tutor'}
                    searchPlaceholder="Search tutors by name or title"
                    emptyMessage={!hasTutorOptions ? 'No approved tutors available yet.' : 'No tutors match your search.'}
                    helperText={
                      !hasTutorOptions && !isTutorsLoading
                        ? isAdmin
                          ? 'Create or approve a tutor profile in the Tutors section before saving this course.'
                          : 'Ask an admin to approve a tutor profile before saving this course.'
                        : 'Only approved tutor profiles appear here.'
                    }
                    error={errors.tutorId?.message}
                    disabled={isTutorSelectionDisabled}
                  />
                )}
              />
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
              Sample Instruction
            </p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">Topic and subtopic example</h2>
            <div className="mt-5 space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Topic 1</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">Introduction to Creative Coding</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-700">1.5 hr</span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-3">
                    <p className="font-semibold text-slate-900">Subtopic 1.1 - Tools and Setup</p>
                    <p className="mt-1 text-sm text-slate-600">Explain the editor, workflow, and first setup steps.</p>
                  </div>
                  <div className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-3">
                    <p className="font-semibold text-slate-900">Subtopic 1.2 - First Interactive Exercise</p>
                    <p className="mt-1 text-sm text-slate-600">Guide learners through a simple hands-on activity.</p>
                  </div>
                </div>
              </div>
              <p className="text-sm leading-6 text-slate-600">
                This visual sample shows the intended nested pattern: one topic with a single duration, plus optional
                subtopics without their own duration fields.
              </p>
            </div>
          </Card>

          <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Curriculum Builder</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Add topics with optional subtopics. Duration belongs only to topics.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  Total: {totalDurationHours.toFixed(1)} hr
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-5">
              {sections.length === 0 ? (
                <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center text-sm text-slate-600">
                  No topics yet. Add one to build the course outline.
                </div>
              ) : (
                sections.map((section, sectionIndex) => (
                  <TopicFields
                    key={section.id}
                    sectionIndex={sectionIndex}
                    register={register}
                    control={control}
                    removeSection={removeSection}
                  />
                ))
              )}
            </div>
            <div className="mt-6 border-t border-slate-200 pt-5">
              <div className="flex justify-center sm:justify-end">
                <Button type="button" variant="secondary" onClick={handleAddTopic}>
                  <Plus className="h-4 w-4" />
                  Add Topic
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {submitError ? <p className="status-message border-red-100 bg-red-50 text-red-700">{submitError}</p> : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => navigate(basePath)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading || isTutorSelectionDisabled}>
            {isLoading ? 'Saving...' : isEditMode ? 'Update Course' : 'Create Course'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const TopicFields = ({ sectionIndex, register, control, removeSection }: any) => {
  const { fields: lessons, append: appendLesson, remove: removeLesson } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.lessons`,
  });

  return (
    <div id={`topic-panel-${sectionIndex}`} className="rounded-[1.5rem] border border-slate-200 bg-slate-50/90 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 items-start gap-3">
          <GripVertical className="mt-3 h-5 w-5 shrink-0 text-slate-400" />
          <div className="grid flex-1 gap-4 md:grid-cols-[minmax(0,1fr)_140px]">
            <Input
              label={`Topic ${sectionIndex + 1}`}
              placeholder="Topic title"
              {...register(`sections.${sectionIndex}.title`, { required: true })}
            />
            <Input
              label="Duration (hours)"
              type="number"
              min={0}
              step={0.5}
              placeholder="0"
              {...register(`sections.${sectionIndex}.durationHours`, { valueAsNumber: true })}
            />
          </div>
        </div>
        <Button type="button" variant="danger" size="sm" onClick={() => removeSection(sectionIndex)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-4">
        <label className="form-label">Topic Description</label>
        <textarea
          className="form-control min-h-[96px]"
          placeholder="What does this topic cover?"
          {...register(`sections.${sectionIndex}.description`)}
        />
      </div>

      <div className="mt-5 space-y-3">
        {lessons.map((lesson, lessonIndex) => (
          <div key={lesson.id} className="rounded-[1.25rem] border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Input
                  label={`Subtopic ${sectionIndex + 1}.${lessonIndex + 1}`}
                  placeholder="Subtopic title"
                  {...register(`sections.${sectionIndex}.lessons.${lessonIndex}.title`, { required: true })}
                />
              </div>
              <Button type="button" variant="ghost" size="sm" className="mt-8" onClick={() => removeLesson(lessonIndex)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            <div className="mt-4">
              <label className="form-label">Subtopic Description</label>
              <textarea
                className="form-control min-h-[84px]"
                placeholder="Optional explanation for this subtopic"
                {...register(`sections.${sectionIndex}.lessons.${lessonIndex}.description`)}
              />
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            appendLesson({
              title: '',
              description: '',
              duration: 0,
              position: lessons.length + 1,
              isPreview: false,
            })
          }
        >
          <Plus className="h-4 w-4" />
          Add Subtopic
        </Button>
      </div>
    </div>
  );
};
